import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Stack,
  Box,
  Avatar,
  Tabs,
  Tab,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Collapse,
  CircularProgress,
  styled,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

import { showErrorMessage } from '@store/dashboard';
import {
  getMethodMatrix,
  selectGroupedMethodMatrixData,
} from '../../store/payment-options-method-matrix';

import DataTable from './MethodMatrixDataTable';

import { getLobLogo } from '../../../../utils/utils';

const MethodMatrixLobCardWrapper = styled(Stack)`
  border: 1px solid ${({ theme }) => theme.palette.grey[300]};
  border-radius: 4px;
  border-radius: 4px;
  margin-bottom: 16px;
`;

const Header = styled(ListItemButton)`
  box-shadow: 0 4px ${({ theme }) => theme.palette.grey[300]};
  border-radius: 4px;
  position: sticky;
  top: 0;
  background: white;
  z-index: 9;
`;

export default function MethodMatrixLobCard({
  lob: { lobId, lobCode, lobName },
  forceExpand,
}) {
  const dispatch = useDispatch();

  const matrixData = useSelector(selectGroupedMethodMatrixData);

  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);

  const platforms = matrixData[lobId] || {};
  const platformKeys = Object.keys(platforms);

  const getMatrixData = useCallback(() => {
    setIsLoading(true);

    dispatch(getMethodMatrix(lobId))
      .then(({ meta: { requestStatus, condition } }) => {
        if (requestStatus !== 'fulfilled' && !condition) {
          const message = `Oops! Something went wrong while fetching the details for ${lobName}`;
          dispatch(showErrorMessage({ message, duration: 4000 }));
          return;
        }

        setIsExpanded(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch, lobId, lobName]);

  useEffect(() => {
    if (!forceExpand) return;

    getMatrixData();
  }, [forceExpand, getMatrixData]);

  const onToggleView = () => {
    if (platformKeys.length === 0) {
      getMatrixData();
      return;
    }

    setIsExpanded((prev) => !prev);
  };

  const onTabChange = (_, tab) => setSelectedTab(tab);

  const getTableData = (methods) =>
    methods.reduce(
      (data, { currencyCode, methodName }) => {
        data.currencies.push(currencyCode);
        data.methodNames.push(methodName);

        return data;
      },
      {
        currencies: [],
        methodNames: [],
      },
    );

  const renderHeaderIcon = () => {
    if (isLoading) return <CircularProgress size={28} />;
    if (isExpanded) return <ExpandLessIcon />;

    return <ExpandMoreIcon />;
  };

  return (
    <MethodMatrixLobCardWrapper>
      <Header onClick={onToggleView}>
        <ListItemAvatar>
          <Avatar
            src={getLobLogo(lobCode)}
            sx={{ width: 30, height: 30 }}
          />
        </ListItemAvatar>
        <ListItemText
          primary={lobName}
          secondary={lobCode}
        />
        {renderHeaderIcon()}
      </Header>
      <Collapse
        in={isExpanded && !isLoading}
        timeout="auto"
        unmountOnExit>
        <Tabs
          variant="scrollable"
          scrollButtons="auto"
          value={selectedTab}
          onChange={onTabChange}>
          {platformKeys.map((platform) => (
            <Tab
              key={platform}
              label={platform}
            />
          ))}
        </Tabs>
        {platformKeys.map((platform, _i) => {
          const methods = platforms[platform];
          const { currencies, methodNames } = getTableData(methods);

          return (
            <Box
              key={platform}
              hidden={selectedTab !== _i}>
              <DataTable
                methods={methods}
                currencies={[...new Set(currencies)]}
                methodNames={[...new Set(methodNames)]}
              />
            </Box>
          );
        })}
      </Collapse>
    </MethodMatrixLobCardWrapper>
  );
}
